import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import Editor from 'react-simple-code-editor';
import Prism from 'prismjs';
import 'prismjs/components/prism-markup';
import 'prismjs/components/prism-markup-templating';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-yaml';
import 'prismjs/components/prism-markdown';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-lua';
import 'prismjs/components/prism-ini';
import 'prismjs/components/prism-toml';
import 'prismjs/components/prism-properties';
import 'prismjs/components/prism-log';
import 'prismjs/components/prism-sql';
import 'prismjs/components/prism-php';
import 'prismjs/themes/prism-tomorrow.css';

const detectLanguage = (path) => {
  if (!path) return 'javascript';
  const lower = path.toLowerCase();
  if (lower.endsWith('.json') || lower.endsWith('.json5')) return 'json';
  if (lower.endsWith('.yml') || lower.endsWith('.yaml')) return 'yaml';
  if (lower.endsWith('.md') || lower.endsWith('.mdx')) return 'markdown';
  if (lower.endsWith('.css') || lower.endsWith('.scss')) return 'css';
  if (lower.endsWith('.ts') || lower.endsWith('.tsx')) return 'typescript';
  if (lower.endsWith('.js') || lower.endsWith('.jsx') || lower.endsWith('.mjs') || lower.endsWith('.cjs')) return 'javascript';
  if (lower.endsWith('.java')) return 'java';
  if (lower.endsWith('.py')) return 'python';
  if (lower.endsWith('.lua')) return 'lua';
  if (lower.endsWith('.sh') || lower.endsWith('.bash') || lower.endsWith('.zsh')) return 'bash';
  if (lower.endsWith('.ini') || lower.endsWith('.cfg') || lower.endsWith('.conf')) return 'ini';
  if (lower.endsWith('.toml')) return 'toml';
  if (lower.endsWith('.properties')) return 'properties';
  if (lower.endsWith('.sql')) return 'sql';
  if (lower.endsWith('.php')) return 'php';
  if (lower.endsWith('.log')) return 'log';
  if (lower.endsWith('.xml') || lower.endsWith('.html') || lower.endsWith('.htm') || lower.endsWith('.svg')) return 'markup';
  if (lower.endsWith('.txt') || lower.endsWith('.text')) return 'plain';
  return 'javascript';
};

const languages = [
  'auto', 'javascript', 'typescript', 'json', 'yaml', 'markdown', 'css',
  'java', 'python', 'lua', 'bash', 'ini', 'toml', 'properties', 'sql',
  'php', 'markup', 'log', 'plain'
];

const BRACKET_PAIRS = { '(': ')', '[': ']', '{': '}' };
const CLOSE_TO_OPEN = { ')': '(', ']': '[', '}': '{' };

const validateBrackets = (code, commentPatterns = { line: '//', blockStart: '/*', blockEnd: '*/' }) => {
  const errors = [];
  const stack = [];
  const lines = code.split('\n');
  let inBlock = false;
  let inString = false;
  let stringChar = '';

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!inBlock && commentPatterns.line) {
      const commentIdx = line.indexOf(commentPatterns.line);
      if (commentIdx === 0) continue;
    }

    for (let j = 0; j < line.length; j++) {
      const ch = line[j];
      const prev = j > 0 ? line[j - 1] : '';

      if (inBlock) {
        if (commentPatterns.blockEnd && line.slice(j, j + commentPatterns.blockEnd.length) === commentPatterns.blockEnd) {
          inBlock = false;
          j += commentPatterns.blockEnd.length - 1;
        }
        continue;
      }

      if (inString) {
        if (ch === stringChar && prev !== '\\') inString = false;
        continue;
      }

      if (ch === '"' || ch === "'" || ch === '`') {
        inString = true;
        stringChar = ch;
        continue;
      }

      if (commentPatterns.line && line.slice(j, j + commentPatterns.line.length) === commentPatterns.line) break;
      if (commentPatterns.blockStart && line.slice(j, j + commentPatterns.blockStart.length) === commentPatterns.blockStart) {
        inBlock = true;
        j += commentPatterns.blockStart.length - 1;
        continue;
      }

      if (BRACKET_PAIRS[ch]) {
        stack.push({ char: ch, line: i + 1 });
      } else if (CLOSE_TO_OPEN[ch]) {
        if (stack.length && stack[stack.length - 1].char === CLOSE_TO_OPEN[ch]) {
          stack.pop();
        } else {
          errors.push({ line: i + 1, message: `Unexpected '${ch}'`, type: 'error' });
        }
      }
    }
  }

  for (const unclosed of stack) {
    errors.push({ line: unclosed.line, message: `Unclosed '${unclosed.char}'`, type: 'error' });
  }
  return errors;
};

const validateSyntax = (code, language) => {
  if (!code.trim()) return [];

  if (language === 'json') {
    try {
      JSON.parse(code);
    } catch (err) {
      const match = err.message.match(/position\s+(\d+)/i);
      if (match) {
        const pos = parseInt(match[1], 10);
        const before = code.slice(0, pos);
        const line = (before.match(/\n/g) || []).length + 1;
        return [{ line, message: err.message, type: 'error' }];
      }
      const lineMatch = err.message.match(/line\s+(\d+)/i);
      if (lineMatch) {
        return [{ line: parseInt(lineMatch[1], 10), message: err.message, type: 'error' }];
      }
      return [{ line: 1, message: err.message, type: 'error' }];
    }
    return [];
  }

  if (language === 'yaml') {
    const lines = code.split('\n');
    const errors = [];
    for (let i = 0; i < lines.length; i++) {
      const raw = lines[i];
      if (!raw.trim() || raw.trim().startsWith('#')) continue;
      if (raw.includes('\t')) {
        errors.push({ line: i + 1, message: 'Tabs are not allowed in YAML', type: 'error' });
      }
      const indent = raw.search(/\S/);
      if (indent > 0 && indent % 2 !== 0 && !raw.trim().startsWith('-')) {
        errors.push({ line: i + 1, message: 'Odd indentation (YAML typically uses 2-space indent)', type: 'warning' });
      }
    }
    return errors;
  }

  if (language === 'markup') {
    const errors = [];
    const tagStack = [];
    const selfClosing = new Set(['area','base','br','col','embed','hr','img','input','link','meta','param','source','track','wbr']);
    const lines = code.split('\n');
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      for (const mt of line.matchAll(/<\/?([a-zA-Z][\w.-]*)[^>]*\/?>/g)) {
        const full = mt[0];
        const name = mt[1].toLowerCase();
        if (selfClosing.has(name) || full.endsWith('/>')) continue;
        if (full.startsWith('</')) {
          if (tagStack.length && tagStack[tagStack.length - 1].name === name) {
            tagStack.pop();
          } else {
            errors.push({ line: i + 1, message: `Unexpected closing tag </${name}>`, type: 'error' });
          }
        } else {
          tagStack.push({ name, line: i + 1 });
        }
      }
    }
    for (const unclosed of tagStack) {
      errors.push({ line: unclosed.line, message: `Unclosed <${unclosed.name}> tag`, type: 'error' });
    }
    return errors;
  }

  if (language === 'javascript' || language === 'typescript') {
    return validateBrackets(code, { line: '//', blockStart: '/*', blockEnd: '*/' });
  }

  if (language === 'css') {
    const errors = validateBrackets(code, { line: null, blockStart: '/*', blockEnd: '*/' });
    const lines = code.split('\n');
    for (let i = 0; i < lines.length; i++) {
      const trimmed = lines[i].trim();
      if (!trimmed || trimmed.startsWith('/*') || trimmed.startsWith('*') || trimmed === '}' || trimmed.startsWith('@') || trimmed.startsWith('//')) continue;
      if (trimmed.includes(':') && !trimmed.includes('{') && !trimmed.endsWith(';') && !trimmed.endsWith(',') && !trimmed.endsWith('}')) {
        errors.push({ line: i + 1, message: 'Missing semicolon', type: 'warning' });
      }
    }
    return errors;
  }

  if (language === 'java' || language === 'php') {
    return validateBrackets(code, { line: '//', blockStart: '/*', blockEnd: '*/' });
  }

  if (language === 'python') {
    const errors = [];
    const lines = code.split('\n');
    let prevIndent = 0;
    let expectIndent = false;
    for (let i = 0; i < lines.length; i++) {
      const raw = lines[i];
      if (!raw.trim() || raw.trim().startsWith('#')) continue;
      if (raw.match(/^\s* \t/) || raw.match(/^\s*\t /)) {
        errors.push({ line: i + 1, message: 'Mixed tabs and spaces in indentation', type: 'error' });
      }
      const indent = raw.search(/\S/);
      if (expectIndent && indent <= prevIndent) {
        errors.push({ line: i + 1, message: 'Expected indented block', type: 'error' });
      }
      expectIndent = raw.trim().endsWith(':');
      prevIndent = indent;
    }
    return [...errors, ...validateBrackets(code, { line: '#', blockStart: null, blockEnd: null })];
  }

  if (language === 'lua') {
    const errors = [];
    const lines = code.split('\n');
    let blockDepth = 0;
    for (let i = 0; i < lines.length; i++) {
      const trimmed = lines[i].replace(/--\[\[[\s\S]*?\]\]/g, '').replace(/--.*/g, '').trim();
      const opens = (trimmed.match(/\b(function|if|for|while|repeat|do)\b/g) || []).length;
      const closes = (trimmed.match(/\b(end|until)\b/g) || []).length;
      blockDepth += opens - closes;
      if (blockDepth < 0) {
        errors.push({ line: i + 1, message: 'Unexpected end/until without matching block', type: 'error' });
        blockDepth = 0;
      }
    }
    if (blockDepth > 0) {
      errors.push({ line: lines.length, message: `${blockDepth} unclosed block(s) — missing 'end'`, type: 'error' });
    }
    return [...errors, ...validateBrackets(code, { line: '--', blockStart: '--[[', blockEnd: ']]' })];
  }

  if (language === 'toml') {
    const errors = [];
    const lines = code.split('\n');
    for (let i = 0; i < lines.length; i++) {
      const trimmed = lines[i].trim();
      if (!trimmed || trimmed.startsWith('#')) continue;
      if (trimmed.startsWith('[')) {
        if (!trimmed.match(/^\[[\w."\-\s[\]]+\]\s*(#.*)?$/)) {
          errors.push({ line: i + 1, message: 'Malformed section header', type: 'error' });
        }
      } else if (!trimmed.includes('=') && !trimmed.startsWith(']')) {
        errors.push({ line: i + 1, message: 'Expected key = value', type: 'warning' });
      }
    }
    return [...errors, ...validateBrackets(code, { line: '#', blockStart: null, blockEnd: null })];
  }

  if (language === 'properties' || language === 'ini') {
    const errors = [];
    const lines = code.split('\n');
    for (let i = 0; i < lines.length; i++) {
      const trimmed = lines[i].trim();
      if (!trimmed || trimmed.startsWith('#') || trimmed.startsWith(';') || trimmed.startsWith('!')) continue;
      if (language === 'ini' && trimmed.startsWith('[')) {
        if (!trimmed.match(/^\[.+\]\s*(;.*)?$/)) {
          errors.push({ line: i + 1, message: 'Malformed section header', type: 'error' });
        }
        continue;
      }
      if (!trimmed.includes('=') && !trimmed.includes(':')) {
        errors.push({ line: i + 1, message: 'Expected key=value or key:value', type: 'warning' });
      }
    }
    return errors;
  }

  if (language === 'bash') {
    const errors = [];
    const lines = code.split('\n');
    let ifDepth = 0;
    for (let i = 0; i < lines.length; i++) {
      const trimmed = lines[i].replace(/#.*/g, '').trim();
      if (!trimmed) continue;
      if (trimmed.match(/\bif\b/) && !trimmed.includes('elif')) ifDepth++;
      if (trimmed === 'fi') ifDepth--;
      if (ifDepth < 0) {
        errors.push({ line: i + 1, message: "Unexpected 'fi' without matching 'if'", type: 'error' });
        ifDepth = 0;
      }
    }
    if (ifDepth > 0) {
      errors.push({ line: lines.length, message: `${ifDepth} unclosed 'if' — missing 'fi'`, type: 'error' });
    }
    return [...errors, ...validateBrackets(code, { line: '#', blockStart: null, blockEnd: null })];
  }

  return [];
};

const CodeEditor = ({
  value,
  onChange,
  path,
  languageOverride = 'auto',
  onLanguageChange = () => {},
  markers = [],
  softWrap = true,
  onToggleWrap = () => {},
  onSave
}) => {
  const language = languageOverride === 'auto' ? detectLanguage(path) : languageOverride;
  const lines = value.split('\n');
  const editorRef = useRef(null);
  const scrollRef = useRef(null);

  const syntaxErrors = useMemo(() => validateSyntax(value, language), [value, language]);
  const allMarkers = useMemo(() => {
    const combined = [...markers];
    for (const err of syntaxErrors) {
      if (!combined.some((m) => m.line === err.line)) combined.push(err);
    }
    return combined;
  }, [markers, syntaxErrors]);
  const errorLines = useMemo(() => new Set(syntaxErrors.map((e) => e.line)), [syntaxErrors]);

  const [showFind, setShowFind] = useState(false);
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [showReplace, setShowReplace] = useState(false);
  const [matchCount, setMatchCount] = useState(0);
  const [currentMatch, setCurrentMatch] = useState(0);
  const [caseSensitive, setCaseSensitive] = useState(false);

  const [cursorInfo, setCursorInfo] = useState({ line: 1, col: 1 });

  useEffect(() => {
    if (!findText || !showFind) { setMatchCount(0); setCurrentMatch(0); return; }
    const flags = caseSensitive ? 'g' : 'gi';
    const escaped = findText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    try {
      const regex = new RegExp(escaped, flags);
      const matches = value.match(regex);
      setMatchCount(matches ? matches.length : 0);
      if (!matches) setCurrentMatch(0);
      else if (currentMatch > matches.length) setCurrentMatch(matches.length);
      else if (currentMatch === 0 && matches.length > 0) setCurrentMatch(1);
    } catch {
      setMatchCount(0);
    }
  }, [findText, value, caseSensitive, showFind]);

  const handleFindNext = useCallback(() => {
    if (!findText || matchCount === 0) return;
    setCurrentMatch((prev) => prev >= matchCount ? 1 : prev + 1);
  }, [findText, matchCount]);

  const handleReplaceCurrent = useCallback(() => {
    if (!findText || matchCount === 0) return;
    const flags = caseSensitive ? 'g' : 'gi';
    const escaped = findText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    try {
      const regex = new RegExp(escaped, flags);
      let idx = 0;
      const target = currentMatch || 1;
      const result = value.replace(regex, (match) => {
        idx++;
        return idx === target ? replaceText : match;
      });
      onChange(result);
    } catch { /* ignore */ }
  }, [findText, replaceText, value, onChange, currentMatch, caseSensitive, matchCount]);

  const handleReplaceAll = useCallback(() => {
    if (!findText) return;
    const flags = caseSensitive ? 'g' : 'gi';
    const escaped = findText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    try {
      const regex = new RegExp(escaped, flags);
      onChange(value.replace(regex, replaceText));
    } catch { /* ignore */ }
  }, [findText, replaceText, value, onChange, caseSensitive]);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return undefined;
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (onSave) onSave();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        setShowFind(true);
        setShowReplace(false);
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'h') {
        e.preventDefault();
        setShowFind(true);
        setShowReplace(true);
      }
      if (e.key === 'Escape' && showFind) {
        setShowFind(false);
      }
    };
    el.addEventListener('keydown', handleKeyDown);
    return () => el.removeEventListener('keydown', handleKeyDown);
  }, [onSave, showFind]);

  const handleClick = useCallback(() => {
    setTimeout(() => {
      const el = editorRef.current?.querySelector('textarea');
      if (!el) return;
      const pos = el.selectionStart;
      const before = value.slice(0, pos);
      const line = (before.match(/\n/g) || []).length + 1;
      const lastNewline = before.lastIndexOf('\n');
      const col = pos - lastNewline;
      setCursorInfo({ line, col });
    }, 0);
  }, [value]);

  const handleKeyUp = useCallback(() => {
    handleClick();
  }, [handleClick]);

  const highlight = useCallback((code) => {
    const highlighted = language === 'plain'
      ? code
      : Prism.highlight(code, Prism.languages[language] || Prism.languages.javascript, language);

    if (errorLines.size === 0) return highlighted;

    return highlighted.split('\n').map((line, i) => {
      if (errorLines.has(i + 1)) {
        const errInfo = syntaxErrors.find((e) => e.line === i + 1);
        return `<span class="code-error-line" title="${(errInfo?.message || 'Syntax error').replace(/"/g, '&quot;')}">${line}</span>`;
      }
      return line;
    }).join('\n');
  }, [language, errorLines, syntaxErrors]);

  return (
    <div className="h-full w-full rounded-xl border border-border bg-[#11141b] overflow-hidden flex flex-col" ref={editorRef}>
      <div className="flex items-center justify-between px-4 py-2.5 text-sm font-semibold text-textMuted border-b border-border gap-3">
        <span className="truncate text-textPrimary/80">{path || 'Untitled'}</span>
        <div className="flex items-center gap-3 text-sm">
          <select
            className="rounded-md border border-border bg-[#0f1219] px-2.5 py-1.5 text-white text-sm focus:border-accent focus:outline-none"
            value={languageOverride}
            onChange={(e) => onLanguageChange && onLanguageChange(e.target.value)}>
            {languages.map((lang) =>
              <option key={lang} value={lang}>
                {lang === 'auto' ? `Auto (${language})` : lang}
              </option>
            )}
          </select>
          <button
            type="button"
            className="ghost-button px-2.5 py-1.5 text-sm"
            onClick={onToggleWrap}>
            {softWrap ? 'No Wrap' : 'Wrap'}
          </button>
          <button
            type="button"
            className="ghost-button px-2.5 py-1.5 text-sm"
            onClick={() => { setShowFind((v) => !v); setShowReplace(false); }}
            title="Find (Ctrl+F)">
            Find
          </button>
        </div>
      </div>

      {showFind &&
        <div className="flex flex-col gap-1.5 px-4 py-2.5 border-b border-border bg-[#0f1219]">
          <div className="flex items-center gap-2">
            <input
              type="text"
              className="flex-1 rounded-md border border-border bg-[#11141b] px-3 py-1.5 text-sm text-white placeholder:text-textMuted focus:border-accent focus:outline-none"
              placeholder="Find..."
              value={findText}
              onChange={(e) => setFindText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleFindNext(); }}
              autoFocus />
            <button
              type="button"
              className={`ghost-button px-2 py-1 text-xs ${caseSensitive ? 'border-accent text-accent' : ''}`}
              onClick={() => setCaseSensitive((v) => !v)}
              title="Case sensitive">
              Aa
            </button>
            <span className="text-xs text-textMuted min-w-[60px] text-center">
              {matchCount > 0 ? `${currentMatch} / ${matchCount}` : 'No results'}
            </span>
            <button type="button" className="ghost-button px-2 py-1 text-sm" onClick={handleFindNext}>Next</button>
            <button
              type="button"
              className="ghost-button px-2 py-1 text-sm"
              onClick={() => setShowReplace((v) => !v)}>
              {showReplace ? 'Hide Replace' : 'Replace'}
            </button>
            <button type="button" className="ghost-button px-2 py-1 text-sm" onClick={() => setShowFind(false)}>Close</button>
          </div>
          {showReplace &&
            <div className="flex items-center gap-2">
              <input
                type="text"
                className="flex-1 rounded-md border border-border bg-[#11141b] px-3 py-1.5 text-sm text-white placeholder:text-textMuted focus:border-accent focus:outline-none"
                placeholder="Replace with..."
                value={replaceText}
                onChange={(e) => setReplaceText(e.target.value)} />
              <button type="button" className="ghost-button px-2 py-1 text-sm" onClick={handleReplaceCurrent}>Replace</button>
              <button type="button" className="ghost-button px-2 py-1 text-sm" onClick={handleReplaceAll}>All</button>
            </div>
          }
        </div>
      }

      <div className="flex-1 min-h-0 overflow-auto font-mono text-base text-white" ref={scrollRef}>
        <div className="flex min-h-full">
          <div className="select-none sticky left-0 z-10 border-r border-border bg-[#10131a] px-3 py-4 text-sm text-textMuted/60 leading-[1.6] text-right min-w-[3.5rem]">
            {lines.map((_, idx) =>
              <div key={idx} className={errorLines.has(idx + 1) ? 'text-red-400' : ''}>{idx + 1}</div>
            )}
          </div>
          <div className="flex-1 relative" onClick={handleClick} onKeyUp={handleKeyUp}>
            <Editor
              value={value}
              onValueChange={onChange}
              highlight={highlight}
              padding={16}
              tabSize={2}
              insertSpaces
              textareaClassName="outline-none"
              preClassName={`language-${language}`}
              style={{
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace',
                fontSize: '1rem',
                lineHeight: '1.6',
                minHeight: '100%',
                background: 'transparent',
                whiteSpace: softWrap ? 'pre-wrap' : 'pre',
                overflowWrap: softWrap ? 'anywhere' : 'normal'
              }} />

            <div className="absolute top-0 right-0 h-full w-1.5 bg-border/40">
              {allMarkers.map((m) =>
                <div
                  key={`${m.line}-${m.type || 'mark'}`}
                  className={`w-full ${m.type === 'error' ? 'bg-red-400' : m.type === 'warning' ? 'bg-yellow-400' : 'bg-accent'}`}
                  style={{ position: 'absolute', top: `${m.line / Math.max(lines.length, 1) * 100}%`, height: '6px' }}
                  title={m.message || ''} />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between px-4 py-1.5 text-xs text-textMuted/70 border-t border-border bg-[#0d1017]">
        <div className="flex items-center gap-4">
          <span>Ln {cursorInfo.line}, Col {cursorInfo.col}</span>
          <span>{lines.length} lines</span>
          <span>{value.length} chars</span>
          {syntaxErrors.length > 0 && (
            <span className="text-red-400">
              {syntaxErrors.length} {syntaxErrors.length === 1 ? 'error' : 'errors'}
              {syntaxErrors[0] && ` — Ln ${syntaxErrors[0].line}: ${syntaxErrors[0].message}`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span>{language}</span>
          <span>UTF-8</span>
          <span className="text-textMuted/50">Ctrl+S Save | Ctrl+F Find | Ctrl+H Replace</span>
        </div>
      </div>
    </div>);
};

export default CodeEditor;
